import { cn } from "@/lib/utils";

interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
}

export function Skeleton({ className, ...props }: SkeletonProps) {
  return (
    <div
      className={cn(
        "animate-pulse rounded-md bg-muted/40",
        className
      )}
      {...props}
    />
  );
}

export function ProductCardSkeleton() {
  return (
    <div className="flex flex-col h-full rounded-xl overflow-hidden border border-solid border-[#d8e0f0]">
      {/* Header */}
      <div className="h-[46px] flex w-full items-center justify-center px-0 py-3.5 bg-muted/20 rounded-[12px_12px_0px_0px] border-b-[0.8px] border-[#d8e0f0]">
        <Skeleton className="w-1/2 h-4" />
      </div>

      {/* Content */}
      <div className="p-0 h-[318px] flex flex-col">
        <div className="flex items-center gap-2.5 p-2.5">
          <div className="flex items-center justify-center gap-2.5 flex-1">
            <Skeleton className="w-3/4 h-4" />
          </div>
        </div>

        <div className="px-3">
          <Skeleton className="w-full h-[127px] rounded-md" />
        </div>

        <div className="flex-1 w-full px-[15px] pt-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex items-center gap-2.5 py-2.5 w-full">
              <Skeleton className="w-4 h-4" />
              <Skeleton className="flex-1 h-4" />
              <Skeleton className="w-6 h-4" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function ProductsGridSkeleton() {
  return (
    <div 
      className="grid auto-rows-[364px] gap-[20px] p-[25px]"
      style={{
        gridTemplateColumns: 'repeat(auto-fill, minmax(256px, 1fr))',
        gridAutoFlow: 'dense'
      }}
    >
      {Array(6).fill(0).map((_, i) => (
        <ProductCardSkeleton key={i} />
      ))}
    </div>
  );
}

export function HeaderSkeleton() {
  return (
    <header className="flex flex-col items-start justify-center gap-2.5 pl-2.5 pr-0 py-2.5 w-full">
      <Skeleton className="w-1/3 h-8" />
      <Skeleton className="w-1/4 h-6 mt-2" />
    </header>
  );
}

export function SearchBarSkeleton() {
  return (
    <div className="flex items-start gap-5 w-full">
      <Skeleton className="flex-1 h-11 rounded-xl" />
      <Skeleton className="w-[120px] h-11 rounded-xl" />
    </div>
  );
}

export function TabsSkeleton() {
  return (
    <div className="w-full bg-clarofrente-01 rounded-xl shadow-[0px_2px_5px_#676e7614,0px_1px_1px_#0000001f] my-[15px] py-3 px-5">
      <div className="flex w-full gap-4">
        {Array(6).fill(0).map((_, i) => (
          <Skeleton key={i} className="h-8 w-24" />
        ))}
      </div>
    </div>
  );
}

export function OutrosByAnimaSkeleton() {
  return (
    <section className="flex flex-col w-full items-center p-[30px] bg-clarofundo-01 shadow-[0px_2px_5px_#676e7614,0px_1px_1px_#0000001f] relative">
      <HeaderSkeleton />
      <TabsSkeleton />
      <div className="flex flex-col items-start gap-5 w-full flex-1">
        <SearchBarSkeleton />
        <div className="w-full bg-clarofrente-01 rounded-2xl overflow-hidden border-0 shadow-none">
          <div className="flex flex-col h-[746px] p-0 relative">
            <ProductsGridSkeleton />
          </div>
        </div>
      </div>
    </section>
  );
} 